import {ParamComponent} from 'panel';
import {produce} from 'immer';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  FlatSortConfig,
  FlatValueSortConfig,
  SortBy,
  SortByValueConfig,
  SortConfig,
  SortOrder,
} from 'common/report/util/sorting/types';
import {
  ColumnGroup,
  Columns,
  DataColumn,
  isTimeComparisonColumnType,
  SegmentColumn,
  SortChangeEvent,
  TableColumn,
  TimeComparisonColumnType,
} from 'common/components/data-table/types';
import {isDataColumn, isSegmentColumn} from 'common/components/data-table/utils';
import {ChartType} from 'common/report/constants';
import {getReportName} from 'common/report/util';

import {template} from './template';
import css from './index.inline.styl';
import {TrackingEvent} from './tracking';

type DataSortMenuState = TODO;

interface DataSortMenuParams {
  sortConfig: SortConfig;
  defaultSortConfig: SortConfig;
  columns: Columns;
  onSortChange?: (ev: SortChangeEvent) => void;
  isLoading?: boolean;
  chartType?: ChartType;
}

type AsyncButtonJsxProps = {
  params?: DataSortMenuParams;
};

export class DataSortMenu extends ParamComponent<DataSortMenuParams, DataSortMenuState> {
  get config() {
    return Object.assign(super.config, {
      css,
      template,
      defaultState: {},
      useShadowDom: true,
      params: {
        isLoading: Boolean,
        sortConfig: {
          type: Object,
          required: true,
        },
        defaultSortConfig: {
          type: Object,
          required: true,
        },
        columns: {
          type: Array,
          required: true,
        },
        chartType: String,
        onSortChange: Function,
      },
    });
  }

  getSegmentAndDataColumns() {
    const {columns} = this.params;

    // when dealing with a multi metric table displaying several columns
    // under one metric group, the assumption here is that the first column
    // defines the sorting criteria
    const getFirstColumn = (column: TableColumn | ColumnGroup): TableColumn => {
      if (`columns` in column) {
        return column.columns[0];
      } else {
        return column;
      }
    };

    const segmentColumns = columns
      ?.filter((column) => isSegmentColumn(getFirstColumn(column)))
      .map(getFirstColumn) as SegmentColumn[];

    const dataColumns = columns
      ?.filter((column) => isDataColumn(getFirstColumn(column)))
      // For time comparison columns, we only want to have the Current column (not Past) in the sort menu
      .filter(
        (column) =>
          !isTimeComparisonColumnType(column) ||
          column.timeComparisonMetadata?.timeComparisonColumnType === TimeComparisonColumnType.Current,
      )
      .map(getFirstColumn) as DataColumn[];

    return {segmentColumns, dataColumns};
  }

  /**
   * Toggles between grouped and flattened sorting
   *
   * @see handleColumnSortChange
   * @see handleValueSortChange
   */
  handleColumnSortingToggle() {
    const {valueField: currentValueField, sortOrder: currentValueSortOrder} = this.params
      .sortConfig as SortByValueConfig;

    const sortConfig = produce(this.params.sortConfig, (draft) => {
      // clear out flattened sorting fields
      let sortBy: SortBy;
      if (draft.sortBy === SortBy.Value) {
        sortBy = SortBy.Column;
        delete draft.valueField;
        delete draft.sortOrder;
      } else {
        sortBy = SortBy.Value;
      }

      draft.sortBy = sortBy;
      if (draft.sortBy === SortBy.Value) {
        const valueFields = this.getSegmentAndDataColumns().dataColumns?.map((column) => column.field);
        draft.valueField = currentValueField in valueFields ? currentValueField : valueFields[0];
        draft.sortOrder = currentValueSortOrder ?? SortOrder.Descending;
      }
    });

    this.dispatchSortChange(sortConfig);
    mixpanel.track(TrackingEvent.ToggledSortingMode, this.getSortConfigTrackingProps(sortConfig));
  }

  /**
   * Updates sorting to be based on multiple columns, in a grouped way.
   * For instance, sort the table data by the Country A-Z and then
   * by City on a descending value order
   */
  handleColumnSortChange(index: number, sortConfig: FlatSortConfig) {
    const currentColConfig = this.params.sortConfig.colSortAttrs[index];

    const updatedSortConfig = produce(this.params.sortConfig, (draft) => {
      draft.colSortAttrs[index] = {
        // retain fields on existing FlatSortConfig (e.g. viewNLimit) except for the ones provided by the updated
        // sortConfig
        ...omit(draft.colSortAttrs[index], [`sortBy`, `sortField`, `valueField`]),
        ...sortConfig,
      };
      draft.sortBy = SortBy.Column;
    });

    this.dispatchSortChange(updatedSortConfig);
    mixpanel.track(TrackingEvent.UpdatedColumn, {
      'column index': index,
      'sort by': sortConfig.sortBy,
      'sort order': sortConfig.sortOrder,
      'sort value': (sortConfig as FlatValueSortConfig).valueField,
      'changed sort order': sortConfig.sortOrder !== currentColConfig.sortOrder,
      'changed sort value':
        (sortConfig as FlatValueSortConfig).valueField !== (currentColConfig as FlatValueSortConfig).valueField,
      ...this.getSortConfigTrackingProps(updatedSortConfig),
    });
  }

  /**
   * Updates sorting to be based on a single column, for instance:
   * sort the table data only by Country A-Z, or by a given metric value
   */
  handleValueSortChange(sortConfig: FlatValueSortConfig) {
    const currentConfig = this.params.sortConfig as FlatValueSortConfig;

    const updatedSortConfig = produce(this.params.sortConfig as SortByValueConfig, (draft) => {
      draft.sortBy = SortBy.Value;
      draft.valueField = sortConfig.valueField;
      draft.sortOrder = sortConfig.sortOrder;
    });

    this.dispatchSortChange(updatedSortConfig);
    mixpanel.track(TrackingEvent.UpdatedFlattenedSorting, {
      'changed sort order': updatedSortConfig.sortOrder !== currentConfig.sortOrder,
      'changed sort value': updatedSortConfig.valueField !== currentConfig.valueField,
      ...this.getSortConfigTrackingProps(updatedSortConfig),
    });
  }

  getSortConfigWithoutViewN(sortConfig: SortConfig): SortConfig {
    if (!sortConfig) {
      return null;
    }

    return produce(sortConfig, (draft) => {
      if (draft.sortBy === SortBy.Value) {
        delete draft.viewNLimit;
      }
      draft.colSortAttrs.forEach((colSortAttr) => {
        delete colSortAttr.viewNLimit;
      });
    });
  }

  isUsingDefaultSort() {
    return isEqual(
      this.getSortConfigWithoutViewN(this.params.defaultSortConfig),
      this.getSortConfigWithoutViewN(this.params.sortConfig),
    );
  }

  handleResetClick(): void {
    const {defaultSortConfig} = this.params;
    mixpanel.track(TrackingEvent.ResetSort, {
      'Old sort config': this.getSortConfigTrackingProps(this.params.sortConfig),
      'Default sort config': this.getSortConfigTrackingProps(defaultSortConfig),
    });
    this.dispatchSortChange(defaultSortConfig);
  }

  dispatchSortChange(sortConfig: SortConfig) {
    const sortChangeEvent: SortChangeEvent = new CustomEvent(`sortChange`, {
      detail: {sorting: sortConfig},
    });
    this.params.onSortChange(sortChangeEvent);
  }

  getSortConfigTrackingProps(sortConfig: SortConfig) {
    const columns = this.getSegmentAndDataColumns();
    const props = {
      tab: getReportName(),
      'Chart type': this.params.chartType,
      'sort type': sortConfig.sortBy === SortBy.Column ? `grouped` : `flattened`,
      '# of metrics': columns?.dataColumns.length,
      '# of breakdowns': columns?.segmentColumns.length,
      'is changing default sort': this.isUsingDefaultSort(),
    };

    if (sortConfig.sortBy === SortBy.Column) {
      props[`sort columns`] = sortConfig.colSortAttrs.map((colSort, index) => {
        const column = columns.segmentColumns[index];
        return {
          'content type': column.contentType,
          'sort order': colSort.sortOrder,
          'sort value': colSort.sortBy,
        };
      });
    } else {
      props[`sort order`] = sortConfig.sortOrder;
      props[`sort value`] = sortConfig.valueField;
    }

    return props;
  }
}

export const DataSortMenuEl = defineAndMakeJsxFactory<AsyncButtonJsxProps>(`mp-data-sort-menu`, DataSortMenu);
export const DataSortMenuReact = panel2react<AsyncButtonJsxProps>(`mp-data-sort-menu`, DataSortMenu);
